"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Code2, Factory, Briefcase, Server, ArrowRight, Building2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Industries() {
  const shouldReduceMotion = useReducedMotion();
  const industries = [{
    icon: Code2,
    title: "SaaS and technology companies",
    description: "Software buying journeys require product-led search architecture. Strategy focuses on high-intent feature queries, comparison pages, and content that supports trial sign-ups and demo requests.",
    points: ["Product-led search content structured around specific user problems and workflows", "Comparison content and versus-alternative pages for evaluators actively shortlisting software", "Targeted queries such as category software for specific industry verticals", "Feature detail, deployment, and implementation content supporting trials and product demos"],
    link: {
      to: "/TechnologyAndSaasPage",
      text: "Technology & SaaS SEO"
    }
  }, {
    icon: Factory,
    title: "Manufacturing and industrial companies",
    description: "Industrial purchases involve long consideration cycles, technical specifications, and strict compliance reviews by procurement and engineering teams.",
    points: ["In-depth technical catalogues, component specifications, and engineering data sheets", "Specialist application pages showing equipment suitability across industrial operating environments", "Content structured for long sales cycles involving both engineering evaluators and commercial procurement leads", "Dedicated maintenance agreement resources and detailed installation guide documentation"],
    link: null
  }, {
    icon: Briefcase,
    title: "Professional services and consultancies",
    description: "Advisory firms in legal, accounting, and management consultancy depend on authority, partner credentials, and demonstrated strategic methodology to earn advisory mandates.",
    points: ["Authority-led search positioning demonstrating deep subject matter expertise and strategic insight", "Clear service pages outlining advisory scope, commercial engagement models, and deliverables", "Sector-specific landing pages addressing regulatory, compliance, and corporate challenges", "Methodology explanations that give senior decision-makers confidence during partner evaluation"],
    link: {
      to: "/B2BProfessionalServicesPage",
      text: "B2B & Professional Services"
    }
  }, {
    icon: Server,
    title: "IT, software providers and B2B service companies",
    description: "Commercial IT service providers and specialised B2B vendors must satisfy stringent operational criteria before securing commercial contracts.",
    points: ["Solution pages closely aligned with IT leadership, operations, and information security pain points", "Comprehensive service level agreement (SLA) information and uptime performance details", "Rigorous data security and regulatory compliance documentation critical to vendor risk assessments", "Integration documentation explaining compatibility with existing enterprise software stacks"],
    link: null
  }];
  return <section id="b2bindustries" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Building2 className="w-3.5 h-3.5 mr-1.5" />
            Sector-Specific Execution
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            SEO for B2B companies in different industries
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            The core principles of commercial search optimisation stay
            consistent, but the strategic execution changes with your industry,
            sales cycle length, and the specific way your buyers research and
            shortlist suppliers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
          {industries.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.title} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.1
          }} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                          <Icon className="size-5" />
                        </div>
                        <CardTitle asChild className="text-xl font-semibold text-foreground">
                          <h3 className="text-xl font-semibold text-foreground font-default">
                            {item.title}
                          </h3>
                        </CardTitle>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </CardHeader>
                    <CardContent className="pt-2">
                      <ul className="space-y-2.5">
                        {item.points.map((pt, pIdx) => <li key={pIdx} className="flex items-start gap-2 text-sm text-foreground/90 leading-relaxed">
                            <span className="size-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            <span>{pt}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </div>
                  {item.link && <div className="px-6 pb-6 pt-3 border-t border-border/40 mt-4">
                      <Link to={item.link.to} className="inline-flex items-center text-sm font-medium text-primary hover:text-primary/80 underline underline-offset-4">
                        Explore {item.link.text}
                        <ArrowRight className="size-3.5 ml-1" />
                      </Link>
                    </div>}
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Looking to align your search strategy with your sector&apos;s buyer journey?
          </p>
          <Button asChild size="lg" className="w-full sm:w-auto rounded-full font-medium">
            <Link to="/ContactPage">
              Book a Free SEO Consultation
              <ArrowRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}